import type { PivotAggregation } from "./types";

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

export class Accumulator {
  count = 0;
  /** Values that parsed as numbers; sum, avg, min and max use this, not `count` */
  numericCount = 0;
  sum = 0;
  min = Infinity;
  max = -Infinity;
  numbers: number[] | null;
  distinct: Set<unknown> | null;

  constructor(needsNumbers: boolean, needsDistinct: boolean) {
    this.numbers = needsNumbers ? [] : null;
    this.distinct = needsDistinct ? new Set() : null;
  }

  add(value: unknown) {
    if (value === null || value === undefined || value === "") return;
    this.distinct?.add(value);
    this.count += 1;
    const n = toNumber(value);
    if (n === null) return;
    this.numericCount += 1;
    this.sum += n;
    if (n < this.min) this.min = n;
    if (n > this.max) this.max = n;
    this.numbers?.push(n);
  }

  merge(other: Accumulator) {
    this.count += other.count;
    this.numericCount += other.numericCount;
    this.sum += other.sum;
    this.min = Math.min(this.min, other.min);
    this.max = Math.max(this.max, other.max);
    if (this.numbers && other.numbers) {
      for (const n of other.numbers) this.numbers.push(n);
    }
    other.distinct?.forEach((value) => this.distinct?.add(value));
  }

  result(aggregation: PivotAggregation): number | null {
    switch (aggregation) {
      case "count":
        return this.count;
      case "distinctCount":
        return this.distinct?.size ?? 0;
      case "sum":
        return this.numericCount ? this.sum : null;
      case "avg":
        return this.numericCount ? this.sum / this.numericCount : null;
      case "min":
        return this.numericCount ? this.min : null;
      case "max":
        return this.numericCount ? this.max : null;
      case "median": {
        if (!this.numbers?.length) return null;
        const sorted = [...this.numbers].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2
          ? sorted[mid]
          : (sorted[mid - 1] + sorted[mid]) / 2;
      }
    }
  }
}

export const AGGREGATION_LABELS: Record<PivotAggregation, string> = {
  sum: "Sum",
  avg: "Average",
  count: "Count",
  distinctCount: "Distinct count",
  min: "Min",
  max: "Max",
  median: "Median",
};
